import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Box, Stack, Card, Tabs, Tab, Typography, } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useStore, useTokens } from '@mcn-platform/models';
import AboutContent from './aboutContent/AboutContent';
import InstructorsContent from './instructorsContent/InstructorsContent';
import SyllabusContent from './syllabusContent/SyllabusContent';
import { WysiwygViewer } from '../../../publisher/wysiwyg/WysiwygEditor';
import PanelTitle from './PanelTitle';
import DiscussionGroup from '../../discussionGroup/DiscussionGroup';
export const richTextConfigItems = {
    richText1: {
        name: 'Show Rich Text 1',
        description: 'Show the item info on the card',
        tokenKey: 'richText1Label',
    },
    richText2: {
        name: 'Show Rich Text 2',
        description: 'Show the item info on the card',
        tokenKey: 'richText2Label',
    },
    richText3: {
        name: 'Show Rich Text 3',
        description: 'Show the item info on the card',
        tokenKey: 'richText3Label',
    },
    richText4: {
        name: 'Show Rich Text 4',
        description: 'Show the item info on the card',
        tokenKey: 'richText4Label',
    },
    richText5: {
        name: 'Show Rich Text 5',
        description: 'Show the item info on the card',
        tokenKey: 'richText5Label',
    },
    richText6: {
        name: 'Show Rich Text 6',
        description: 'Show the item info on the card',
        tokenKey: 'richText6Label',
    },
    richText7: {
        name: 'Show Rich Text 7',
        description: 'Show the item info on the card',
        tokenKey: 'richText7Label',
    },
    richText8: {
        name: 'Show Rich Text 8',
        description: 'Show the item info on the card',
        tokenKey: 'richText8Label',
    },
    richText9: {
        name: 'Show Rich Text 9',
        description: 'Show the item info on the card',
        tokenKey: 'richText9Label',
    },
    richText10: {
        name: 'Show Rich Text 10',
        description: 'Show the item info on the card',
        tokenKey: 'richText10Label',
    },
};
export const tabSettingsDefault = [
    {
        key: 'about',
        active: true,
    },
    {
        key: 'instructors',
        active: true,
    },
    {
        key: 'outline',
        active: true,
    },
    {
        key: 'richText1',
        active: false,
    },
    {
        key: 'richText2',
        active: false,
    },
    {
        key: 'richText3',
        active: false,
    },
    {
        key: 'richText4',
        active: false,
    },
    {
        key: 'richText5',
        active: false,
    },
    {
        key: 'richText6',
        active: false,
    },
    {
        key: 'richText7',
        active: false,
    },
    {
        key: 'richText8',
        active: false,
    },
    {
        key: 'richText9',
        active: false,
    },
    {
        key: 'richText10',
        active: false,
    },
];
export const showTab = (tabSetting, item) => {
    if (tabSetting.active === false) {
        return false;
    }
    if (!item) {
        // show all enabled tabs if there is no course
        return true;
    }
    const { key } = tabSetting;
    if (key.startsWith('richText') && (!item[key] || !item[key].slate || item[key].slate === '')) {
        return false;
    }
    return true;
};
const ItemTabsWidget = ({ itemType, itemID, tabConfigItems, tabSettings, progressBarColor, }) => {
    const [activeTab, setActiveTab] = useState(0);
    const { getOrLoad, isAnonymous } = useStore();
    const tokens = useTokens();
    const item = itemID ? getOrLoad(itemType, itemID) : null;
    const handleTabClick = (event, newValue) => {
        setActiveTab(newValue);
        const element = document.getElementById(`div-${newValue}`);
        if (element) {
            window.scrollTo({
                behavior: 'smooth',
                top: element.getBoundingClientRect().top
                    - document.body.getBoundingClientRect().top
                    - 48,
            });
        }
    };
    if (item?.syncState !== 'loaded') {
        return null;
    }
    const tabs = tabSettings?.reduce((activeTabs, tabSetting) => {
        // if this tab should be displayed, add it to the list.
        if (!showTab(tabSetting, item)) {
            return activeTabs;
        }
        let component;
        const { key } = tabSetting;
        const tabConfig = tabConfigItems[key];
        const title = tokens.getToken(tabConfig.tokenKey);
        if (key === 'about') {
            component = itemType === 'course' ? (_jsx(AboutContent, { course: item, action: () => { }, buttonLabel: "", showPrice: false })) : (_jsx(AboutContent, { program: item, action: () => { }, buttonLabel: "", showPrice: false }));
        }
        if (key === 'instructors') {
            component = (_jsx(InstructorsContent, { instructors: item?.instructors, settings: {
                    showTitle: true,
                    showInstructors: true,
                    instructorsCount: item?.instructors?.length,
                } }));
        }
        if (key === 'outline') {
            component = (_jsx(SyllabusContent, { sections: item?.sections, item: item, titleTokenKey: `${itemType}OutlineLabel`, title: "Course Outline", progressBarColor: progressBarColor }));
        }
        if (key === 'discussion' && item?.discussionTopic && !isAnonymous) {
            component = (_jsx(DiscussionGroup, { topic: item?.discussionTopic }));
        }
        if (key.startsWith('richText')) {
            component = (_jsxs(_Fragment, { children: [_jsx(PanelTitle, { title: title }), _jsx(Typography, { className: "landingpage-section-text", children: _jsx(WysiwygViewer, { id: key, value: item && item[key] ? item[key].slate : null }) })] }));
        }
        // if the component hasnt been defined, dont show the tab
        if (component) {
            activeTabs.push({
                tabConfig,
                key,
                title,
                component,
            });
        }
        return activeTabs;
    }, []);
    return (_jsxs(Box, { sx: {
            width: '100%',
        }, children: [_jsx(Box, { sx: (t) => {
                    const backgroundColor = t.portal.landingPage.tabs.background.color || t.palette.background.default;
                    return ({
                        flexGrow: 1,
                        mb: 1,
                        position: 'sticky',
                        top: 0,
                        backgroundColor: backgroundColor,
                        zIndex: 1,
                    });
                }, children: _jsx(Box, { display: "flex", justifyContent: "center", width: "100%", children: _jsx(Tabs, { variant: "scrollable", scrollButtons: "auto", onChange: handleTabClick, value: activeTab, children: tabs && tabs.map((tab) => (_jsx(Tab, { label: tab.title, className: "courseInfoTabs", sx: {
                                display: 'flex',
                                borderRadius: '5px',
                                boxShadow: '0 0 6px 0 #cccc',
                                margin: '4px',
                                fontSize: '12px',
                                fontWeight: '700',
                                minHeight: '45px',
                            } }, `tablink-${tab.key}`))) }) }) }), _jsx(Box, { children: tabs && tabs.map((tab, index) => (_jsx(Stack, { sx: { mb: 1, scrollPaddingTop: '150px' }, id: `div-${index}`, children: _jsx(Card, { sx: { p: 2 }, children: tab.component }) }, tab.key))) })] }));
};
ItemTabsWidget.getChildren = (config) => (config.props.tabs.map((tab) => (tab.config)));
export default observer(ItemTabsWidget);
//# sourceMappingURL=ItemTabsWidget.js.map